import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '@/lib/utils'

type ItemProps = React.ComponentProps<'div'> & {
  asChild?: boolean
  /** Linha interativa (data-toggle do DS): cursor, hover, pressed e foco visível. */
  interactive?: boolean
  /** `.br-item.selected` do DS: fundo --selected com tinta clara. */
  selected?: boolean
  /** `.br-item.active` do DS (o "activated" do UIKIT): fundo --active. */
  active?: boolean
}

// Nos estados de fundo escuro o DS não repinta o texto: ele troca o bloco de
// tokens de tinta para as versões `-dark`, e quem consome (--color, --hover,
// --focus-color…) segue igual.
const inkDarkTokens = [
  '[--color:var(--color-dark)] [--color-rgb:var(--color-dark-rgb)]',
  '[--interactive:var(--interactive-dark)]',
  '[--interactive-rgb:var(--interactive-dark-rgb)]',
  '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
  '[--focus-color:var(--focus-color-dark)]',
].join(' ')

function Item({
  className,
  asChild = false,
  interactive = false,
  selected = false,
  active = false,
  ...props
}: ItemProps) {
  const Comp = asChild ? Slot : 'div'
  return (
    <Comp
      data-slot="item"
      data-interactive={interactive || undefined}
      data-selected={selected || undefined}
      data-active={active || undefined}
      className={cn(
        // --item-padding-* e --focus-offset são tokens do .br-item: ficam
        // servidos aqui e apenas consumidos.
        '[--item-padding-x:var(--spacing-scale-2x)]',
        '[--item-padding-y:var(--spacing-scale-base)]',
        '[--focus-offset:calc(var(--focus-width)*-1)]',
        'block w-full text-left',
        'bg-[var(--background)] text-[var(--color)]',
        'px-[var(--item-padding-x)] py-[var(--item-padding-y)]',
        selected && ['bg-[var(--selected)]', inkDarkTokens],
        active && ['bg-[var(--active)]', inkDarkTokens],
        interactive && [
          'cursor-pointer',
          'transition-[background-image] [transition-duration:var(--duration-fast)]',
          // Sobreposição sutil no hover e mais forte no pressed (rgba de
          // --color-rgb nas opacidades --hover/--pressed), padrão do DS.
          '[&:not(:disabled):hover]:[background-image:linear-gradient(rgba(var(--color-rgb),var(--hover)),rgba(var(--color-rgb),var(--hover)))]',
          '[&:not(:disabled):active]:[background-image:linear-gradient(rgba(var(--color-rgb),var(--pressed)),rgba(var(--color-rgb),var(--pressed)))]',
          // Foco visível: outline tracejado dourado com o offset interno do DS.
          'outline-none',
          'focus-visible:outline focus-visible:[outline-style:var(--focus-style)]',
          'focus-visible:[outline-width:var(--focus-width)]',
          'focus-visible:[outline-offset:var(--focus-offset)]',
          'focus-visible:[outline-color:var(--focus-color)]',
        ],
        // Desabilitado: --disabled, tanto no <button>/<a> quanto via
        // aria-disabled quando a linha não é um controle nativo.
        'disabled:cursor-not-allowed disabled:[opacity:var(--disabled)]',
        'aria-disabled:cursor-not-allowed aria-disabled:[opacity:var(--disabled)]',
        className
      )}
      {...props}
    />
  )
}

export { Item }
